import React, { useState, useEffect, useCallback, useRef, useImperativeHandle, forwardRef, useMemo } from "react";
import {
  View,
  Text,
  StyleSheet,
  Platform,
  Dimensions,
  TouchableOpacity,
} from "react-native";
import { useDispatch, useSelector } from "react-redux";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import LHFilter, { DropDownOption } from "../../../components/LHFilter";
import LHDataTable from "../../../components/LHDataTable";
import DetailOverlay from "./DetailOverlay";
import { WALK_OBSERVATIONS_TABLE_COLUMNS } from "../constants";
import { getWalkObservationFilterOptions } from "../getWalkObservationFilterOptions";
import { fetchWalkObservationsData } from "../../../components/racking_inspection/fetchWalkObservationsData";
import { formatDate } from "../../../utils/dateFormatter";
import {
  setFilters,
  resetFilters,
} from "../../../store/slices/rackingWalkObservationsSlice";
import { fetchWalkObservationFilterOptions } from "../../../store/slices/walkObservationFilterOptionsThunks";
import { RootState, AppDispatch } from "../../../store";
import { formatUserName } from "../../../utils/emailTouserName";
import FontFamily from "../../../constants/fonts";
import { isIOSMobile } from "../../sparcwalk/utils/deviceDetection";
import { useTheme } from "../../../theme";

// Module-level navigation tracker (outside component)
let moduleNavigationTracker = {
  hasBeenVisited: false,
  lastUnmountTime: 0,
};
const formatPeriodWeek = (item: any): string => {
  const period = item?.period ?? "";
  const week = item?.week ?? "";
  if (period && week) {
    return `P${period}W${week}`;
  }
  if (period) {
    return `P${period}`;
  }
  if (week) {
    return `W${week}`;
  }
  return "-";
};

interface WalkObservationsTabProps {
  isOverlayVisible?: boolean;
  selectedRowData?: Record<string, any> | null;
  closeOverlay?: () => void;
  onRowClick?: (rowData: any) => void;
  onFilterChange?: (
    key: string,
    value: DropDownOption | DropDownOption[] | null
  ) => void;
  onBeginPageOpen?: (
    uid: string,
    walkStartedBy: string,
    status?: number
  ) => void;
  // Mobile filter props
  showMobileFilterButton?: boolean;
  onMobileFilterPress?: () => void;
  onMobileFilterProceed?: () => void;
  // Mobile filter trigger for prop-based filtering
  mobileFilterTrigger?: {
    filters: Record<string, any>;
    timestamp: number;
  } | null;
  // Reset filters trigger for iOS mobile issue
  resetFiltersTrigger?: number;
  // Reset mobile filter overlay UI (not the applied filters)
  resetMobileFiltersTrigger?: number;
}

const WalkObservationsTab = forwardRef<any, WalkObservationsTabProps>(({
  isOverlayVisible = false,
  selectedRowData = null,
  closeOverlay = () => {},
  onRowClick = () => {},
  onFilterChange = () => {},
  onBeginPageOpen,
  showMobileFilterButton = false,
  onMobileFilterPress,
  onMobileFilterProceed,
  mobileFilterTrigger = null,
  resetFiltersTrigger,
  resetMobileFiltersTrigger,
}, ref) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const dispatch: AppDispatch = useDispatch();

  // Local state
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [hasResetFiltersOnMount, setHasResetFiltersOnMount] = useState(false);

  // Redux state
  const { walkObservations, filteredWalkObservations, filters } = useSelector(
    (state: RootState) => state.rackingWalkObservations
  );

  // Get selected location from Redux store
  const selectedLocation = useSelector(
    (state: RootState) => state.appData.selectedLocation
  );

  // Get filter options from Redux store (avoid conditional hooks)
  const filterOptions = useSelector(
    (state: RootState) => state.rackingWalkObservations.filterOptions
  );
  const employees = useSelector((state: RootState) => state.appData.employees);

  // Define a type for walk observation items
  type WalkObservationItem = {
    UID?: string;
    PW?: string;
    Status?: string;
    "Total RS Issue"?: number;
    "Total LC Issues"?: number;
    "Walk Performed Date"?: string;
    "Walk Started BY"?: string;
    "Last Modified Date"?: string;
    "Last Modified By"?: string;
    WalkStatus?: string;
    RSPW?: string;
    RSISSUES?: number;
    LCISSUES?: number;
    DAMAGEISSUES?: number;
    TYPEOFDAMAGE?: string;
    RSWALK_DATE?: string;
    WALK_STARTED_BY?: string;
    RSUsername?: string;
    RSUID?: string;
    LAST_MOD_DATE?: string;
    lastModifiedDate?: string;
    [key: string]: any;
  };

  // Map walkObservations data to the expected format for LHDataTable
  const mapStatus = (status: any) => {
    if (status === 0 || status === "0") {
      return "In Progress";
    }
    if (status === 1 || status === "1") {
      return "Complete";
    }
    return status || "Pending";
  };

  const transformedData = useMemo(() => {
    return (filteredWalkObservations || []).map((item: WalkObservationItem, idx: number) => {
      // Avoid noisy logs in map; keep only useful debugging elsewhere
      // Handle both current API format and new format
      const getTypeOfDamage = (val: any) => {
        if (!val || val === "N/A") {
          return "No Damage Recorded";
        }
        return val;
      };

      if (item.UID || item.PW) {
        // New format (like the sample data provided)
        const stableId = item.UID || `WO_${item.PW || 'unknown'}_${item.RSUID || idx}`;
        return {
          status: mapStatus(item.Status),
          periodweek: item.PW || "N/A",
          totalregissues: item["Total RS Issue"] || 0,
          totalloadcapacityissues: item["Total LC Issues"] || 0,
          totaldamageissues: item["Total DM Damages"] || 0,
          Typeofdamage: getTypeOfDamage(item["DM Damage Types"]),
          walkprefdate: formatDate(item["Walk Performed Date"]),
          walkstartedby: formatUserName(item["Walk Started BY"]) || "Unknown",
          lastmodtime: formatDate(item["Last Modified Date"]),
          lastmodby: formatUserName(item["Last Modified By"]) || "N/A",
          id: stableId,
          action: mapStatus(item.Status),
          rawData: item,
        };
      } else {
        // Original format
        const stableId = item.RSUID || `WO_${item.RSPW || 'unknown'}_${idx}`;
        return {
          status: mapStatus(item.WalkStatus),
          periodweek: item.RSPW,
          totalregissues: item.RSISSUES,
          totalloadcapacityissues: item.LCISSUES || 0,
          totaldamageissues: item.DAMAGEISSUES || 0,
          Typeofdamage: getTypeOfDamage(item.TYPEOFDAMAGE),
          walkprefdate: formatDate(item.RSWALK_DATE),
          walkstartedby: formatUserName(item.WALK_STARTED_BY) || "Unknown",
          lastmodtime: formatDate(item.LAST_MOD_DATE || item.lastModifiedDate),
          lastmodby: formatUserName(item.RSUsername) || "N/A",
          id: stableId,
          action: mapStatus(item.WalkStatus),
          rawData: item,
        };
      }
    });
  }, [filteredWalkObservations]);

  console.log("transformedData length:", transformedData.length);
  console.log("walkObservations length:", walkObservations.length);
  console.log(
    "filteredWalkObservations length:",
    filteredWalkObservations.length
  );

  // Fetch filter options on mount or when location changes
  useEffect(() => {
    if (selectedLocation?.name || selectedLocation?.displayText) {
      dispatch(
        fetchWalkObservationFilterOptions(
          selectedLocation.name || selectedLocation.displayText
        )
      );
    }
  }, [dispatch, selectedLocation]);

  // Smart filter reset logic for iOS mobile - only on true navigation away from module
  useEffect(() => {
    const isIOSMobileDevice = isIOSMobile();
    
    if (!isIOSMobileDevice) return;

    // Use module-level tracker to determine if we're returning from navigation
    const hasActiveFilters = Object.values(filters).some(value => value && value !== "");
    const now = Date.now();
    const timeSinceLastUnmount = now - moduleNavigationTracker.lastUnmountTime;
    
    if (!hasResetFiltersOnMount) {
      // Reset filters if:
      // 1. Has active filters AND
      // 2. Module has been visited before AND 
      // 3. More than 3 seconds since last unmount (indicates navigation away, not tab switch)
      if (hasActiveFilters && moduleNavigationTracker.hasBeenVisited && timeSinceLastUnmount > 3000) {
        console.log("📱 iOS mobile detected - resetting stale filters from previous navigation");
        dispatch(resetFilters());
      }
      
      // Mark module as visited
      moduleNavigationTracker.hasBeenVisited = true;
      setHasResetFiltersOnMount(true);
    }
  }, [dispatch, filters, hasResetFiltersOnMount]);

  // Track unmount timing for navigation detection  
  useEffect(() => {
    const isIOSMobileDevice = isIOSMobile();
    
    if (!isIOSMobileDevice) return;
    
    return () => {
      // Record unmount time for navigation detection
      moduleNavigationTracker.lastUnmountTime = Date.now();
      console.log("📱 iOS mobile - component unmounting, recorded timestamp");
    };
  }, []);

  // Handle reset filters trigger from parent (for navigation-based reset)
  useEffect(() => {
    if (resetFiltersTrigger && resetFiltersTrigger > 0) {
      console.log("📱 Reset filters trigger received - clearing filters");
      dispatch(resetFilters());
    }
  }, [resetFiltersTrigger, dispatch]);

  // Handle reset mobile filter overlay trigger (resets UI, not applied filters)
  useEffect(() => {
    if (resetMobileFiltersTrigger && resetMobileFiltersTrigger > 0) {
      console.log("📱 Reset mobile filter overlay trigger received - will reset filter selections in overlay");
      // This trigger is meant for the parent to know when to reset the overlay UI
      // The actual reset happens in the parent component (MobileFilterOverlay)
    }
  }, [resetMobileFiltersTrigger]);

  // Fetch data on component mount or when selectedLocation changes
  useEffect(() => {
    fetchWalkObservationsData({
      setIsLoading,
      setApiError,
      dispatch,
      location: selectedLocation?.name || selectedLocation?.displayText,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation]);

  // Handle mobile filter trigger from parent (prop-based filtering)
  useEffect(() => {
    if (mobileFilterTrigger?.filters) {
      console.log("📱 Walk Observations useEffect triggered with mobileFilterTrigger:", mobileFilterTrigger);
      
      // Convert normalized filters to Redux format (similar to handleFilterChange)
      const reduxFilters: Record<string, string> = { ...filters };
      
      Object.entries(mobileFilterTrigger.filters).forEach(([key, value]: [string, any]) => {
        if (!value || value === '') {
          // Clear the filter
          let reduxKey: string;
          switch (key) {
            case "period": reduxKey = "period"; break;
            case "week": reduxKey = "week"; break;
            case "year": reduxKey = "year"; break;
            case "status": reduxKey = "status"; break;
            case "created/by": reduxKey = "createdBy"; break;
            case "finalized/by": reduxKey = "finalizedBy"; break;
            case "type/of/damage": reduxKey = "typeOfDamage"; break;
            default: reduxKey = key; break;
          }
          reduxFilters[reduxKey] = "";
          return;
        }

        // Convert filter value to Redux format
        let reduxKey: string;
        let apiValue: string;

        switch (key) {
          case "period":
            reduxKey = "period";
            apiValue = typeof value === 'object' ? String(value.id) : String(value);
            break;
          case "week":
            reduxKey = "week";
            apiValue = typeof value === 'object' ? String(value.id) : String(value);
            break;
          case "year":
            reduxKey = "year";
            apiValue = typeof value === 'object' ? String(value.id) : String(value);
            break;
          case "status":
            reduxKey = "status";
            if (typeof value === 'object' && value.id !== undefined) {
              // Use the id directly since it already matches the API expectations
              apiValue = String(value.id);
            } else {
              apiValue = String(value);
            }
            break;
          case "created/by":
            reduxKey = "createdBy";
            apiValue = typeof value === 'object' ? (value.email || value.displayText) : String(value);
            break;
          case "finalized/by":
            reduxKey = "finalizedBy";
            apiValue = typeof value === 'object' ? (value.email || value.displayText) : String(value);
            break;
          case "type/of/damage":
            reduxKey = "typeOfDamage";
            apiValue = typeof value === 'object' ? value.displayText : String(value);
            break;
          default:
            reduxKey = key;
            apiValue = typeof value === 'object' ? (value.displayText || String(value.id)) : String(value);
            break;
        }

        reduxFilters[reduxKey] = apiValue;
        console.log(`📱 Walk Observations mobile trigger converted ${key} to Redux:`, { reduxKey, apiValue });
      });
      
      // Update Redux state with converted filters
      dispatch(setFilters(reduxFilters));
      
      // Make API call with the original filter objects
      triggerFilteredAPICall(mobileFilterTrigger.filters);
    }
  }, [mobileFilterTrigger]);

  const customizedColumns = React.useMemo(() => {
    return WALK_OBSERVATIONS_TABLE_COLUMNS;
  }, []);

  // Compute iOS mobile boolean inside the component to avoid module-level reference issues
  const windowWidth = Dimensions.get("window").width;
  const isMobileIos = Platform.OS === "ios" && windowWidth < 768;

  // Pagination state for iOS mobile card view
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10; // always show 10 records per mobile page as requested
  const [displayStart, setDisplayStart] = useState(1); // window start for the two-number cells

  useEffect(() => {
    // Reset to first page whenever the table data changes
    setCurrentPage(1);
    setDisplayStart(1);
  }, [transformedData]);

  // Ensure currentPage does not exceed total pages when data shrinks
  const totalPages = Math.max(
    1,
    Math.ceil((transformedData?.length || 0) / rowsPerPage)
  );
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedData = isMobileIos
    ? transformedData.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
      )
    : transformedData;

  // Auto-apply filters with debounce (following LeadershipWalk pattern)
  useEffect(() => {
    const isIOSMobileDevice = isIOSMobile();
    
    // Skip auto-apply for iOS mobile (waits for manual Proceed button)
    if (isIOSMobileDevice) {
      return;
    }

    // Debounce filter changes for Web/iPad
    const debounceTimer = setTimeout(() => {
      triggerFilteredAPICall(filters);
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [filters]);

  // Helper function to trigger API call with current filters
  const triggerFilteredAPICall = async (currentFilters: any) => {
    // Wrap in additional try-catch to prevent external script errors
    try {
      console.log("📡 Making Walk Observations API call with filters:", currentFilters);
      
      // Transform filters to API format
      const transformedFilters: Record<string, any> = {};
      
      Object.entries(currentFilters).forEach(([key, value]: [string, any]) => {
        // Skip empty or null values
        if (!value || value === '' || (typeof value === 'object' && !value.id && !value.displayText)) {
          return;
        }
        
        // Handle dropdown objects vs string values
        let apiValue: string;
        
        switch (key) {
          case "period":
            if (typeof value === 'object' && value.id !== undefined) {
              apiValue = String(value.id);
            } else {
              apiValue = String(value);
            }
            transformedFilters.period = apiValue;
            break;
          case "week":
            if (typeof value === 'object' && value.id !== undefined) {
              apiValue = String(value.id);
            } else {
              apiValue = String(value);
            }
            transformedFilters.week = apiValue;
            console.log("📱 Week filter transformed:", { original: value, apiValue });
            break;
          case "year":
            if (typeof value === 'object' && value.id !== undefined) {
              apiValue = String(value.id);
            } else {
              apiValue = String(value);
            }
            transformedFilters.year = apiValue;
            break;
          case "status":
            if (typeof value === 'object' && value.id !== undefined) {
              // Use the id directly since it already matches the API expectations
              apiValue = String(value.id);
            } else {
              // Handle string values that might be already transformed
              apiValue = String(value);
            }
            transformedFilters.status = apiValue;
            break;
          case "created/by":
          case "createdBy":
            if (typeof value === 'object') {
              apiValue = value.email || value.displayText || String(value.id);
            } else {
              apiValue = String(value);
            }
            transformedFilters.createdBy = apiValue;
            break;
          case "finalized/by":
          case "finalizedBy":
            if (typeof value === 'object') {
              apiValue = value.email || value.displayText || String(value.id);
            } else {
              apiValue = String(value);
            }
            transformedFilters.finalizedBy = apiValue;
            break;
          case "type/of/damage":
          case "typeOfDamage":
            if (typeof value === 'object' && value.displayText) {
              apiValue = value.displayText;
            } else {
              apiValue = String(value);
            }
            transformedFilters.typeOfDamage = apiValue;
            break;
          default:
            // For any other filters, use the value as-is if it's a string
            if (typeof value === 'string') {
              transformedFilters[key] = value;
            } else if (typeof value === 'object' && value.displayText) {
              transformedFilters[key] = value.displayText;
            }
            break;
        }
      });
      
      console.log("📡 Transformed Walk Observations filters for API:", transformedFilters);
      
      await fetchWalkObservationsData({
        setIsLoading: () => {}, // Handled by Redux
        setApiError: () => {}, // Handled by Redux
        dispatch,
        location: selectedLocation?.name || selectedLocation?.displayText,
        filters: transformedFilters,
        applyFilters: true
      });
      
      console.log("✅ Walk Observations filter API call completed");
    } catch (error) {
      console.error("❌ Error in Walk Observations filter API call:", error);
    }
  };

  // Simplified filter change handler - just updates state (following LeadershipWalk pattern)
  const handleFilterChange = (
    key: string,
    value: DropDownOption | DropDownOption[] | null
  ) => {
    console.log("📱 Walk Observations Filter change:", { key, value });
    
    if (!value || Array.isArray(value)) {
      // Clear specific filter
      let reduxKey: string;
      
      switch (key) {
        case "period":
          reduxKey = "period";
          break;
        case "week":
          reduxKey = "week";
          break;
        case "year":
          reduxKey = "year";
          break;
        case "status":
          reduxKey = "status";
          break;
        case "created/by":
          reduxKey = "createdBy";
          break;
        case "finalized/by":
          reduxKey = "finalizedBy";
          break;
        case "type/of/damage":
          reduxKey = "typeOfDamage";
          break;
        default:
          reduxKey = key;
          break;
      }
      
      dispatch(setFilters({ ...filters, [reduxKey]: "" }));
      return;
    }

    // Map filter key to Redux store key and value
    let reduxKey: string;
    let apiValue: string;

    switch (key) {
      case "period":
        reduxKey = "period";
        apiValue = String(value.id);
        break;
      case "week":
        reduxKey = "week";
        apiValue = String(value.id);
        break;
      case "year":
        reduxKey = "year";
        apiValue = String(value.id);
        break;
      case "status":
        reduxKey = "status";
        // Use the id directly since it already matches the API expectations (0 for In-Progress, 1 for Completed)
        apiValue = String(value.id);
        break;
      case "created/by":
        reduxKey = "createdBy";
        apiValue = (value as any).email || value.displayText;
        break;
      case "finalized/by":
        reduxKey = "finalizedBy";
        apiValue = (value as any).email || value.displayText;
        break;
      case "type/of/damage":
        reduxKey = "typeOfDamage";
        apiValue = value.displayText;
        break;
      default:
        console.warn("Unknown Walk Observations filter key:", key);
        return;
    }

    console.log("Setting Walk Observations filter:", { reduxKey, apiValue });
    dispatch(setFilters({ ...filters, [reduxKey]: apiValue }));
  };

  // Function to apply filters and fetch data with filters
  const applyFilters = useCallback(async () => {
    try {
      setIsLoading(true);

      if (!selectedLocation?.name && !selectedLocation?.displayText) {
        throw new Error("No location selected");
      }

      // Clean filters object - remove empty values
      const cleanedFilters = Object.entries(filters).reduce(
        (acc: Record<string, string>, [key, value]) => {
          if (typeof value === "string" && value.trim() !== "") {
            acc[key] = value.trim();
          }
          return acc;
        },
        {}
      );

      console.log("Applying cleaned filters:", cleanedFilters);

      // Fetch walk observations with applied filters
      await fetchWalkObservationsData({
        setIsLoading,
        setApiError,
        dispatch,
        location: selectedLocation?.name || selectedLocation?.displayText,
        filters: cleanedFilters,
        applyFilters: true,
      });
    } catch (err) {
      console.error("Error applying filters:", err);
      setApiError(
        err instanceof Error ? err.message : "Failed to apply filters"
      );
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, filters, selectedLocation]);

  // Function to reset filters and reload data without filters
  const resetFiltersAndFetch = useCallback(async () => {
    try {
      setIsLoading(true);

      if (!selectedLocation?.name && !selectedLocation?.displayText) {
        throw new Error("No location selected");
      }

      // Reset filters in redux
      dispatch(resetFilters());

      // Explicitly pass empty filters object to ensure no filters are applied
      await fetchWalkObservationsData({
        setIsLoading,
        setApiError,
        dispatch,
        location: selectedLocation?.name || selectedLocation?.displayText,
        filters: {},
        applyFilters: false,
      });

      console.log("Reset filters, fetched data without filters");
    } catch (err) {
      console.error("Error resetting filters:", err);
      setApiError(
        err instanceof Error ? err.message : "Failed to reset filters"
      );
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, selectedLocation]);

  // Handle row click to fetch detailed racking inspection data or open begin page
  const handleRowClick = (rowData: any) => {
    console.log("Row clicked:", rowData);
    const uid = rowData.id;
    const walkStartedBy =
      (rowData.rawData &&
        (rowData.rawData["Walk Started BY"] ||
          rowData.rawData.WALK_STARTED_BY)) ||
      rowData.walkstartedby ||
      "";

    if (uid && onBeginPageOpen && rowData.columnKey === "status") {
      const originalStatus =
        rowData.rawData?.Status !== undefined
          ? rowData.rawData.Status
          : rowData.status === "Complete"
          ? 1
          : 0;

      console.log(
        "Calling onBeginPageOpen with uid, walkStartedBy and status:",
        uid,
        walkStartedBy,
        originalStatus
      );
      onBeginPageOpen(uid, walkStartedBy, originalStatus);
      return;
    }

    // Filter out fields we don't want to show in the overlay but preserve rawData for API calls
    const fieldsToExclude = ["status", "id", "columnKey"];
    const filteredRowData = { ...rowData };

    fieldsToExclude.forEach((field) => {
      if (filteredRowData.hasOwnProperty(field) && field !== "rawData") {
        delete filteredRowData[field];
      }
    });

    const dataWithId = {
      ...filteredRowData,
      id: uid,
      rawData: rowData.rawData,
    };
    console.log("Passing data to overlay with rawData:", dataWithId);
    onRowClick(dataWithId);
  };

  // Expose methods for parent component
  useImperativeHandle(ref, () => ({
    handleMobileFilterProceed: async (nextFilters: Record<string, any>) => {
      console.log("📱 Walk Observations handleMobileFilterProceed called with:", nextFilters);
      
      // Convert normalized filters to Redux format (similar to handleFilterChange)
      const reduxFilters: Record<string, string> = { ...filters };
      
      Object.entries(nextFilters).forEach(([key, value]: [string, any]) => {
        if (!value || value === '') {
          // Clear the filter
          let reduxKey: string;
          switch (key) {
            case "period": reduxKey = "period"; break;
            case "week": reduxKey = "week"; break;
            case "year": reduxKey = "year"; break;
            case "status": reduxKey = "status"; break;
            case "created/by": reduxKey = "createdBy"; break;
            case "finalized/by": reduxKey = "finalizedBy"; break;
            case "type/of/damage": reduxKey = "typeOfDamage"; break;
            default: reduxKey = key; break;
          }
          reduxFilters[reduxKey] = "";
          return;
        }

        // Convert filter value to Redux format
        let reduxKey: string;
        let apiValue: string;

        switch (key) {
          case "period":
            reduxKey = "period";
            apiValue = typeof value === 'object' ? String(value.id) : String(value);
            break;
          case "week":
            reduxKey = "week";
            apiValue = typeof value === 'object' ? String(value.id) : String(value);
            break;
          case "year":
            reduxKey = "year";
            apiValue = typeof value === 'object' ? String(value.id) : String(value);
            break;
          case "status":
            reduxKey = "status";
            if (typeof value === 'object' && value.id !== undefined) {
              // Use the id directly since it already matches the API expectations
              apiValue = String(value.id);
            } else {
              apiValue = String(value);
            }
            break;
          case "created/by":
            reduxKey = "createdBy";
            apiValue = typeof value === 'object' ? (value.email || value.displayText) : String(value);
            break;
          case "finalized/by":
            reduxKey = "finalizedBy";
            apiValue = typeof value === 'object' ? (value.email || value.displayText) : String(value);
            break;
          case "type/of/damage":
            reduxKey = "typeOfDamage";
            apiValue = typeof value === 'object' ? value.displayText : String(value);
            break;
          default:
            reduxKey = key;
            apiValue = typeof value === 'object' ? (value.displayText || String(value.id)) : String(value);
            break;
        }

        reduxFilters[reduxKey] = apiValue;
        console.log(`📱 Walk Observations converted ${key} to Redux:`, { reduxKey, apiValue });
      });
      
      // Update Redux state with converted filters
      dispatch(setFilters(reduxFilters));
      
      // Call parent callback
      if (onMobileFilterProceed) {
        onMobileFilterProceed();
      }
      
      try {
        console.log("🚀 Walk Observations making API call with filters:", nextFilters);
        await triggerFilteredAPICall(nextFilters);
        console.log("✅ Walk Observations mobile filter API call completed");
      } catch (error) {
        console.error("❌ Error in Walk Observations mobile filter API call:", error);
      }
    },
    handleFilterChange: (key: string, value: any) => {
      console.log("📱 Walk Observations handleFilterChange called via ref:", { key, value });
      handleFilterChange(key, value);
    },
    resetMobileFilterOverlay: () => {
      console.log("📱 Walk Observations resetMobileFilterOverlay called - overlay should reset selections");
      // This method can be called by parent when opening mobile filter overlay
      // to ensure the overlay starts with fresh/empty selections
      return true; // Return value to confirm reset capability
    }
  }));

  // Memoize filter options to prevent recomputation on every render (following LeadershipWalk pattern)
  const memoizedFilterOptions = useMemo(() => {
    return getWalkObservationFilterOptions(filterOptions, employees, filters);
  }, [filterOptions, employees, filters]);

  return (
    <View style={[styles.metricsWrapper, isDark && { backgroundColor: colors.background.page }]}>
      <View
        style={[
          styles.dataTableContainer,
          isMobileIos && styles.dataTableContainerMobile,
          isDark && { backgroundColor: colors.background.surface },
          isMobileIos && isDark && { backgroundColor: "transparent" },
        ]}
      >
        <View
          style={[
            styles.headerBox,
            showMobileFilterButton && styles.headerBoxMobileSpacing,
            showMobileFilterButton && styles.headerBoxIOSMobile,
          ]}
        >
          <View style={styles.headerTitleContainer}>
            <Text
              style={[
                styles.headerTitle,
                showMobileFilterButton && styles.headerTitleMobile,
                isDark && { color: colors.text.inverse },
              ]}
              numberOfLines={2}
              ellipsizeMode="tail"
            >
              Walk Observations List
            </Text>
          </View>
          {showMobileFilterButton && (
            <TouchableOpacity
              style={[styles.mobileFilterButton, { backgroundColor: colors.brand.primary, borderColor: colors.brand.primary }]}
              onPress={() => {
                console.log("📱 Mobile filter button pressed - requesting overlay reset");
                // Use parent callback for mobile filter
                onMobileFilterPress?.();
              }}
            >
              <MaterialIcons name="filter-list" size={20} color="#FFFFFF" />
              <Text style={styles.mobileFilterButtonText}>Filter</Text>
            </TouchableOpacity>
          )}
        </View>

        {/* Hide regular filters when mobile filter button is shown or iOS mobile */}
        {!showMobileFilterButton && !isMobileIos && (
          <View style={styles.filterContainer}>
            <LHFilter
              filters={memoizedFilterOptions}
              onFilterChange={handleFilterChange}
              itemsPerRow={getResponsiveItemsPerRow(4)}
              onClearAllFilters={resetFiltersAndFetch}
            />
          </View>
        )}

        {isLoading && (
          <View style={styles.messageContainer}>
            <Text style={[styles.loadingText, isDark && { color: colors.brand.primary }]}>
              Loading racking inspection data...
            </Text>
          </View>
        )}

        {apiError && (
          <View style={styles.messageContainer}>
            <Text style={styles.errorText}>Error: {apiError}</Text>
          </View>
        )}

        {!isLoading && (
          <>
            {transformedData.length === 0 ? (
              <View style={styles.messageContainer}>
                <Text style={isDark && { color: colors.text.inverse }}>No records found</Text>
              </View>
            ) : isMobileIos ? (
              // Card layout for iOS mobile
              <View style={styles.cardsContainer}>
                {paginatedData.map((item, index) => (
                  <React.Fragment key={`card-${item.id || 'unknown'}-${index}`}>
                    <CardItem
                      item={item}
                      columns={customizedColumns}
                      onRowClick={handleRowClick}
                    />
                    {index < paginatedData.length - 1 && <CardSeparator />}
                  </React.Fragment>
                ))}

                {/* Mobile pagination controls */}
                <View style={styles.mobilePaginationContainer}>
                  <View style={[styles.mobilePaginationBox, isDark && { borderColor: colors.border.default }]}>
                    <TouchableOpacity
                      disabled={displayStart === 1 && currentPage === 1}
                      onPress={() => {
                          const newPage = Math.max(1, currentPage - 1);
                          console.log('📱 WalkObservations: previous page pressed, newPage=', newPage);
                          setCurrentPage(newPage);
                          setDisplayStart(Math.max(1, newPage));
                        }}
                      style={[
                        styles.mobilePaginationCell,
                        styles.mobilePaginationArrowCell,
                        displayStart === 1 &&
                          currentPage === 1 &&
                          styles.mobilePaginationCellDisabled,
                      ]}
                      testID="mobile-prev-page-button"
                      accessibilityLabel="Previous page"
                    >
                      <MaterialIcons
                        name="chevron-left"
                        size={16}
                        color={
                          displayStart === 1 && currentPage === 1
                            ? "#A0A0A0"
                            : isDark ? colors.text.inverse : "#005CBC"
                        }
                      />
                    </TouchableOpacity>

                    {/* Two-number window: displayStart and displayStart+1 */}
                    {(() => {
                      const a = displayStart;
                      const b = Math.min(totalPages, displayStart + 1);
                      const aIsActive = currentPage === a;
                      const bIsActive = currentPage === b;
                      const isSinglePage = totalPages === 1;

                      return (
                        <>
                          <TouchableOpacity
                            disabled={a > totalPages}
                            onPress={() => {
                              console.log('📱 WalkObservations: page number pressed, pageNum=', a);
                              a <= totalPages && setCurrentPage(a);
                            }}
                            accessibilityLabel={`Go to page ${a}`}
                            style={[
                              styles.mobilePaginationCell,
                              aIsActive
                                ? styles.mobilePaginationCurrentCell
                                : [styles.mobilePaginationNextCell, isDark && { backgroundColor: colors.background.elevated }],
                            ]}
                          >
                            <Text
                              style={
                                aIsActive
                                  ? styles.mobilePaginationCurrentText
                                  : [styles.mobilePaginationNextText, isDark && { color: colors.text.inverse }]
                              }
                            >
                              {a}
                            </Text>
                          </TouchableOpacity>

                          {isSinglePage ? (
                            <View
                              style={[
                                styles.mobilePaginationCell,
                                styles.mobilePaginationEmptyCell,
                                isDark && { backgroundColor: colors.background.elevated },
                              ]}
                            />
                          ) : (
                            <TouchableOpacity
                              disabled={b > totalPages}
                              onPress={() => {
                                console.log('📱 WalkObservations: page number pressed, pageNum=', b);
                                b <= totalPages && setCurrentPage(b);
                              }}
                              accessibilityLabel={`Go to page ${b}`}
                              style={[
                                styles.mobilePaginationCell,
                                bIsActive
                                  ? styles.mobilePaginationCurrentCell
                                  : [styles.mobilePaginationNextCell, isDark && { backgroundColor: colors.background.elevated }],
                              ]}
                            >
                              <Text
                                style={
                                  bIsActive
                                    ? styles.mobilePaginationCurrentText
                                    : [styles.mobilePaginationNextText, isDark && { color: colors.text.inverse }]
                                }
                              >
                                {b}
                              </Text>
                            </TouchableOpacity>
                          )}
                        </>
                      );
                    })()}

                    <TouchableOpacity
                      disabled={
                        displayStart + 1 >= totalPages &&
                        currentPage === totalPages
                      }
                      onPress={() => {
                          const newPage = Math.min(totalPages, currentPage + 1);
                          console.log('📱 WalkObservations: next page pressed, newPage=', newPage);
                          setCurrentPage(newPage);
                          setDisplayStart(newPage);
                        }}
                      style={[
                        styles.mobilePaginationCell,
                        styles.mobilePaginationArrowCell,
                        displayStart + 1 >= totalPages &&
                          currentPage === totalPages &&
                          styles.mobilePaginationCellDisabled,
                      ]}
                      testID="mobile-next-page-button"
                      accessibilityLabel="Next page"
                    >
                      <MaterialIcons
                        name="chevron-right"
                        size={16}
                        color={
                          displayStart + 1 >= totalPages &&
                          currentPage === totalPages
                            ? "#A0A0A0"
                            : isDark ? colors.text.inverse : "#005CBC"
                        }
                      />
                    </TouchableOpacity>
                  </View>
                </View>
              </View>
            ) : (
              // Table layout for web and iPad
              <LHDataTable
                data={transformedData}
                columns={customizedColumns}
                tableHeader=""
                tableDescription={`Showing ${transformedData.length} result(s)`}
                onRowClick={handleRowClick}
              />
            )}
          </>
        )}
      </View>

      {/* Detail Overlay */}
      <DetailOverlay
        visible={isOverlayVisible}
        onDismiss={closeOverlay}
        data={selectedRowData}
        title="Racking Inspection Details"
      />
    </View>
  );
});

const styles = StyleSheet.create({
  metricsWrapper: {
    padding: 0,
    backgroundColor: "#F5F9FF",
    flex: 1,
  },
  headerBox: {
    backgroundColor: "transparent",
    marginTop: 12,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  headerTitleContainer: {
    flex: 1,
    marginRight: 12,
    justifyContent: "center",
  },
  headerTitle: {
    fontFamily: FontFamily.bold,
    fontWeight: "700",
    fontStyle: "normal",
    fontSize: 24,
    lineHeight: 28,
    letterSpacing: 0,
    color: "#3A3A3A",
    textAlign: "left",
    marginBottom: 8,
    flexWrap: "wrap",
  },
  messageContainer: {
    padding: 16,
    alignItems: "center",
  },
  filterContainer: {
    marginTop: 8,
    marginBottom: 8,
  },
  loadingText: {
    fontSize: 16,
    color: "#0066CC",
    fontFamily: FontFamily.regular,
  },
  errorText: {
    fontSize: 16,
    color: "#DC3545",
    fontFamily: FontFamily.regular,
  },
  dataTableContainer: {
    backgroundColor: "#fff",
    borderRadius: 8,
    padding: 24,
  },
  dataTableContainerMobile: {
    // backgroundColor: "transparent",
    borderRadius: 0,
    padding: 0,
  },
  headerBoxMobileSpacing: {
    marginBottom: 0,
  },
  headerBoxIOSMobile: {
    /* keep the same space-between layout but ensure vertical centering on iOS mobile */
    justifyContent: "space-between",
    alignItems: "center",
  },
  cellText: {
    fontSize: 14,
    color: "rgb(97, 97, 97)",
    textAlign: "left",
    flexWrap: "wrap",
    width: "100%",
    padding: 4,
    fontFamily: FontFamily.regular,
  },
  detailsCell: {
    fontSize: 14,
    color: "#005CBC",
    textAlign: "left",
    flexWrap: "wrap",
    width: "100%",
    maxWidth: Platform.OS === "web" ? "100%" : 250,
    fontWeight: 500,
    fontFamily: FontFamily.regular,
  },
  // Card styles for iOS mobile
  cardsContainer: {
    flex: 1,
    backgroundColor: "transparent",
  },
  cardContainer: {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    padding: 16,
    marginBottom: 8,
    ...Platform.select({
      ios: {
        shadowColor: "rgba(0, 94, 192, 0.08)",
        shadowOffset: { width: 4, height: 0 },
        shadowOpacity: 1,
        shadowRadius: 12,
      },
      android: {
        elevation: 4,
        shadowColor: "rgba(0, 94, 192, 0.08)",
      },
      web: {
        boxShadow: "4px 0px 24px -2px rgba(0, 94, 192, 0.08)",
      },
    }),
    position: "relative",
  },
  cardSeparator: {
    height: 8,
  },
  cardHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 12,
  },
  cardPrimaryValue: {
    flex: 1,
    marginRight: 12,
  },
  cardPrimaryText: {
    fontSize: Platform.OS === "web" ? 14 : 12,
    fontWeight: "500",
    color: "#005CBC",
    lineHeight: 20,
    textAlign: "left",
    fontFamily: FontFamily.regular,
  },
  statusTag: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 12,
    minWidth: 50,
    alignItems: "center",
    justifyContent: "center",
  },
  statusTagText: {
    fontSize: 12,
    fontWeight: "400",
    textAlign: "center",
    lineHeight: 18,
    fontFamily: FontFamily.regular,
  },
  statusTagCompleted: {
    backgroundColor: "#28A745",
  },
  statusTagInProgress: {
    backgroundColor: "#E5A408",
  },
  statusTagFailed: {
    backgroundColor: "#C12C01",
  },
  statusTagDefault: {
    backgroundColor: "#6C757D",
  },
  cardContent: {
    marginBottom: 8,
  },
  cardRow: {
    marginBottom: 4,
  },
  cardRowText: {
    fontSize: Platform.OS === "web" ? 14 : 12,
    lineHeight: 20,
    textAlign: "left",
    fontFamily: FontFamily.regular,
  },
  cardRowLabel: {
    fontWeight: "700",
    fontSize: Platform.OS === "web" ? 12 : 12,
    color: "#3A3A3A",
    fontFamily: FontFamily.regular,
  },
  cardRowValue: {
    fontWeight: "400",
    fontSize: Platform.OS === "web" ? 14 : 12,
    color: "#3A3A3A",
    fontFamily: FontFamily.regular,
  },
  viewDetailsContainer: {
    paddingVertical: 4,
    marginBottom: -6,
    zIndex: 10,
  },
  viewDetailsButton: {
    zIndex: 11,
  },
  viewDetailsText: {
    fontSize: 14,
    fontWeight: "500",
    color: "#005CBC",
    lineHeight: 20,
    fontFamily: FontFamily.regular,
  },
  cardTapOverlay: {
    position: "absolute",
    top: 60,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "transparent",
    zIndex: 1,
  },
  // Mobile pagination styles
  mobilePaginationContainer: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 16,
    backgroundColor: "transparent",
  },
  mobilePaginationBox: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "transparent",
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "#D7E5F8",
    overflow: "hidden",
  },
  mobilePaginationCell: {
    minHeight: 32,
    minWidth: 32,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  mobilePaginationArrowCell: {
    backgroundColor: "transparent",
  },
  mobilePaginationCurrentCell: {
    backgroundColor: "#005CBC",
  },
  mobilePaginationNextCell: {
    backgroundColor: "#FBFCFE",
  },
  mobilePaginationEmptyCell: {
    backgroundColor: "#FBFCFE",
    opacity: 0.3,
  },
  mobilePaginationCellDisabled: {
    opacity: 0.5,
  },
  mobilePaginationCurrentText: {
    fontSize: 14,
    fontWeight: "700",
    color: "#FFFFFF",
    textAlign: "center",
    fontFamily: FontFamily.regular,
  },
  mobilePaginationNextText: {
    fontSize: 14,
    fontWeight: "400",
    color: "#005CBC",
    textAlign: "center",
    fontFamily: FontFamily.regular,
  },
  mobileFilterButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#034895",
    borderColor: "#034895",
    borderRadius: 24,
    paddingHorizontal: 16,
    height: 32,
    width: 120,
    minWidth: 60,
    gap: 4,
    /* keep the button inline with the header title (do not offset vertically) */
    zIndex: 2,
    justifyContent: "center",
    marginTop: 14,
  },

  headerTitleMobile: {
    marginBottom: 10,
    marginTop: 4,
  },
  mobileFilterButtonText: {
    fontSize: 14,
    fontWeight: "700",
    color: "#FFFFFF",
    lineHeight: 20,
    fontFamily: FontFamily.regular,
  },
});

const getResponsiveItemsPerRow = (itemsPerRow: number): number => {
  const windowWidth = Dimensions.get("window").width;

  if (Platform.OS === "web") {
    return windowWidth > 1200
      ? Math.min(itemsPerRow, 4)
      : Math.min(itemsPerRow, 3);
  } else if (Platform.OS === "ios" || Platform.OS === "android") {
    if (windowWidth > 768) {
      return Math.min(itemsPerRow, 3);
    }
    return Math.min(itemsPerRow, 2);
  }
  return itemsPerRow;
};

// Card separator component
const CardSeparator = () => <View style={{ height: 8 }} />;

// Card component for iOS mobile
interface CardItemProps {
  item: any;
  columns: any[];
  onRowClick: (rowData: any) => void;
}

const CardItem: React.FC<CardItemProps> = ({ item, columns, onRowClick }) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  // Get the status tag style based on status value
  const getStatusTagStyle = (status: any) => {
    let raw = status ?? "";
    if (raw && typeof raw !== "string" && raw.props && raw.props.children) {
      const children = raw.props.children;
      if (typeof children === "string") {
        raw = children;
      } else if (Array.isArray(children)) {
        raw = children.join("");
      } else {
        raw = String(children);
      }
    }

    const normalized = String(raw)
      .toLowerCase()
      .replace(/[-_\s]/g, "");

    if (normalized === "completed" || normalized === "complete") {
      return styles.statusTagCompleted;
    }
    if (normalized.includes("inprogress") || normalized.includes("progress")) {
      return styles.statusTagInProgress;
    }
    if (normalized === "failed") {
      return styles.statusTagFailed;
    }
    return styles.statusTagDefault;
  };

  // Return explicit background color for a status value
  const getStatusTagColor = (status: any) => {
    let raw = status ?? "";
    if (raw && typeof raw !== "string" && raw.props && raw.props.children) {
      const children = raw.props.children;
      if (typeof children === "string") raw = children;
      else if (Array.isArray(children)) raw = children.join("");
      else raw = String(children);
    }

    const normalized = String(raw)
      .toLowerCase()
      .replace(/[-_\s]/g, "");

    if (normalized === "completed" || normalized === "complete")
      return "#027C06";
    if (normalized.includes("inprogress") || normalized.includes("progress"))
      return "#E5A408";
    if (normalized === "failed") return "#C12C01";
    return "#6C757D";
  };

  // Get the status text color
  const getStatusTextColor = (_status: any) => {
    return "#FFFFFF";
  };

  // For iOS mobile, use Period Week as the clickable primary value
  const periodWeekColumn = columns.find(
    (col) => col.columnKey === "periodweek"
  );
  const periodWeekValue = periodWeekColumn?.renderCell
    ? periodWeekColumn.renderCell(item)
    : item[periodWeekColumn?.columnKey];

  // Last column for status display - find the status column
  const statusColumn = columns.find((col) => col.columnKey === "status");
  let statusValue: any = item[statusColumn?.columnKey] || item.status;
  if (statusValue === undefined || statusValue === null) {
    statusValue = "new";
  }

  // All columns for display (exclude status and periodweek from content)
  const displayColumns = columns.filter(
    (col) => col.columnKey !== "status" && col.columnKey !== "periodweek"
  );

  return (
    <View style={[styles.cardContainer, isDark && { backgroundColor: colors.background.elevated, shadowColor: "transparent" }]}>
      {/* First Row: Primary value (left) + Status tag (right) */}
      <View style={styles.cardHeader}>
        <TouchableOpacity
          onPress={() => {
            const clickData = { ...item, columnKey: "status" };
            onRowClick(clickData);
          }}
          style={styles.cardPrimaryValue}
        >
          <Text style={[styles.cardPrimaryText, isDark && { color: colors.text.inverse }]} numberOfLines={2}>
            {typeof periodWeekValue === "string"
              ? periodWeekValue
              : item[periodWeekColumn?.columnKey]}
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          onPress={() => {
            const clickData = { ...item, columnKey: "status" };
            onRowClick(clickData);
          }}
          style={[
            styles.statusTag,
            getStatusTagStyle(statusValue),
            { backgroundColor: getStatusTagColor(statusValue) },
          ]}
        >
          <Text
            style={[
              styles.statusTagText,
              { color: getStatusTextColor(statusValue) },
            ]}
          >
            {statusValue}
          </Text>
        </TouchableOpacity>
      </View>

      {/* Content Rows: "Header: Value" format - show all fields */}
      <View style={styles.cardContent}>
        {displayColumns.map((column, index) => {
          const value = column.renderCell
            ? column.renderCell(item)
            : item[column.columnKey];

          return (
            <View key={`${column.columnKey}-${index}`} style={styles.cardRow}>
              <Text style={styles.cardRowText}>
                <Text style={[styles.cardRowLabel, isDark && { color: colors.text.secondary }]}>
                  {column.header.toUpperCase()}:{" "}
                </Text>
                <Text style={[styles.cardRowValue, isDark && { color: colors.text.inverse }]}>
                  {typeof value === "string" ? value : value || "-"}
                </Text>
              </Text>
            </View>
          );
        })}
      </View>

      {/* View Details Link - opens overlay when clicked */}
      {displayColumns.length > 0 && (
        <View style={styles.viewDetailsContainer}>
          <TouchableOpacity
            onPress={(e) => {
              e.stopPropagation();
              onRowClick(item);
            }}
            style={styles.viewDetailsButton}
          >
            <Text style={[styles.viewDetailsText, isDark && { color: colors.brand.primary }]}>View Details</Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Tap anywhere else overlay for modal */}
      <TouchableOpacity
        style={[
          styles.cardTapOverlay,
          {
            bottom: displayColumns.length > 0 ? 40 : 0,
          },
        ]}
        onPress={() => onRowClick(item)}
        activeOpacity={0.1}
        hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
      />
    </View>
  );
};

export default WalkObservationsTab;

